const clarity = require('./clarity');

let isDispatching = false;

/**
 * Returns the checkout login modal element if rendered on the current page.
 *
 * @returns {jQuery} the modal element (length 0 when absent)
 */
function getCheckoutLoginModal() {
    return $('#guest-checkout-login');
}

/**
 * Returns the global CSRF error modal element.
 *
 * @returns {jQuery} the modal element (length 0 when absent)
 */
function getCsrfModal() {
    return $('#csrf-error-modal');
}

/**
 * Shows the legacy variant of the CSRF modal with the Refresh button.
 *
 * @param {jQuery} $modal - the CSRF error modal
 * @returns {void}
 */
function showLegacyVariant($modal) {
    $modal.find('.modal-description').addClass('d-none');
    $modal.find('.modal-description-legacy').removeClass('d-none');
    $modal.find('.refresh-page-cta').removeClass('d-none');
    $modal.find('.restore-cart-cta, .refresh-page-link').addClass('d-none');
    $modal.find('.refresh-page-cta')
        .off('click.csrfReload')
        .one('click.csrfReload', reloadPage);
    $modal.one('hidden.bs.modal', function () {
        isDispatching = false;
    });
    $modal.modal('show');
}

/**
 * Shows the guest variant of the CSRF modal. When a basket snapshot exists the
 * primary CTA restores the saved cart and a secondary "Go to cart" link is shown.
 * When no snapshot exists (cookie cleared / unreadable) only the "Go to cart"
 * primary CTA is shown.
 *
 * @param {jQuery} $modal - the CSRF error modal
 * @param {boolean} hasBasketSnapshot - whether a basket snapshot cookie is present
 * @returns {void}
 */
function showGuestRestoreVariant($modal, hasBasketSnapshot) {
    $modal.find('.modal-description').addClass('d-none');
    $modal.find('.modal-description-guest').removeClass('d-none');
    $modal.find('.refresh-page-cta').addClass('d-none');

    if (hasBasketSnapshot) {
        $modal.find('.restore-cart-cta, .go-to-cart-link').removeClass('d-none');
        $modal.find('.go-to-cart-cta').addClass('d-none');
    } else {
        $modal.find('.go-to-cart-cta').removeClass('d-none');
        $modal.find('.restore-cart-cta, .go-to-cart-link').addClass('d-none');
    }

    $modal.modal('show');
}

/**
 * Reloads the current page.
 *
 * @returns {void}
 */
function reloadPage() {
    window.location.reload();
}

/**
 * Navigates to the cart page (used by the guest "Go to cart" CTA / link).
 *
 * @param {jQuery} $modal - the CSRF error modal carrying the `data-cart-url`
 * @returns {void}
 */
function navigateToCart($modal) {
    const url = $modal.data('cart-url');

    if (url) {
        window.location.assign(url);
    } else {
        reloadPage();
    }
}

/**
 * Removes any lingering SFRA spinner overlay (`.veil`) left behind by a failed
 * checkout AJAX call (e.g. the base `updateShippingMethodList` ajax has no
 * error/fail handler, so its element-level spinner is never stopped on a 500).
 * Mirrors SFRA `removeSpinner` so the parent's inline position is also reset.
 *
 * @returns {void}
 */
function removeVeil() {
    const $veils = $('.veil');

    $veils.each(function () {
        const $parent = $(this).parent();

        if ($parent.hasClass('veiled')) {
            $parent.css('position', '');
            $parent.removeClass('veiled');
        }
    });

    $veils.remove();
}

/**
 * Fetches a fresh CSRF token and injects it into the checkout login form.
 *
 * @param {Function} onDone - callback fired after the token swap (success or failure)
 * @returns {void}
 */
function refreshCsrfTokenInLoginForm(onDone) {
    const url = getCsrfModal().data('csrf-token-url');

    if (!url) {
        if (typeof onDone === 'function') {
            onDone();
        }
        return;
    }

    $.ajax({
        url: url,
        type: 'GET',
        dataType: 'json',
        cache: false
    }).done(function (data) {
        if (data && data.tokenName && data.token) {
            const $loginModal = getCheckoutLoginModal();
            $loginModal.find('input[name="' + data.tokenName + '"]').val(data.token);
        }
    }).always(function () {
        if (typeof onDone === 'function') {
            onDone();
        }
    });
}

/**
 * Submits the checkout registered-customer login form directly to its action,
 * bypassing the global `.submit-customer-login` click handler that would route
 * the request through the current checkout stage's submission flow.
 *
 * @param {jQuery} $loginModal - the checkout login modal
 * @returns {void}
 */
function submitExpiredLoginForm($loginModal) {
    const $form = $loginModal.find('#registered-customer');

    if (!$form.length) {
        reloadPage();
        return;
    }

    $.ajax({
        url: $form.attr('action'),
        type: 'POST',
        data: $form.serialize(),
        dataType: 'json'
    }).done(function (data) {
        if (data && data.error && !data.redirectUrl) {
            $loginModal.find('.submit-customer-login').attr('disabled', false);
            return;
        }
        reloadPage();
    }).fail(function () {
        reloadPage();
    });
}

/**
 * Opens `#guest-checkout-login` with the session-expired notice; on login success
 * reloads the page so the rest of the storefront re-syncs with the new session.
 *
 * @returns {void}
 */
function dispatchAuthenticatedRecovery() {
    const $loginModal = getCheckoutLoginModal();

    refreshCsrfTokenInLoginForm(function () {
        $loginModal.addClass('is-session-expired');
        $loginModal.find('.registered-form').removeClass('d-none');
        $loginModal.find('.sign-in-default').addClass('d-none');
        $loginModal.find('.sign-in-expired').removeClass('d-none');

        $loginModal.find('.submit-customer-login')
            .off('click.csrfLogin')
            .on('click.csrfLogin', function (e) {
                e.preventDefault();
                e.stopImmediatePropagation();
                $(this).attr('disabled', true);
                submitExpiredLoginForm($loginModal);
            });

        $loginModal.one('hidden.bs.modal', function () {
            $loginModal.removeClass('is-session-expired');
            $loginModal.find('.sign-in-expired').addClass('d-none');
            $loginModal.find('.sign-in-default').removeClass('d-none');
            $loginModal.find('.submit-customer-login').off('click.csrfLogin');
            isDispatching = false;
        });

        $loginModal.modal('show');
    });
}

/**
 * Shows the guest variant of the CSRF modal and wires the Restore / Refresh CTAs.
 *
 * @param {boolean} hasBasketSnapshot - whether a basket snapshot exists (reported
 *   by the failing CSRF-AjaxFail response), used to pick the popup variant
 * @returns {void}
 */
function dispatchGuestRecovery(hasBasketSnapshot) {
    const $modal = getCsrfModal();
    showGuestRestoreVariant($modal, hasBasketSnapshot);

    $modal.one('hidden.bs.modal', function () {
        isDispatching = false;
    });

    $modal.find('.restore-cart-cta').off('click.csrfRestore').one('click.csrfRestore', function () {
        const url = $modal.data('restore-cart-url');

        if (!url) {
            navigateToCart($modal);
            return;
        }

        // The endpoint reads the cookie at click time, so it is the source of
        // truth even if the render-time `data-has-snapshot` hint is stale
        $.ajax({
            url: url,
            type: 'POST',
            dataType: 'json'
        }).done(function (data) {
            if (data && data.success && data.items > 0) {
                // Items restored into the new basket - reload checkout in place.
                reloadPage();
            } else {
                // Nothing to restore (no/empty snapshot) - send the user to the
                // cart instead of reloading into an empty checkout.
                navigateToCart($modal);
            }
        }).fail(function () {
            navigateToCart($modal);
        });
    });

    $modal.find('.go-to-cart-cta, .go-to-cart-link').off('click.csrfGoCart').one('click.csrfGoCart', function (e) {
        e.preventDefault();
        navigateToCart($modal);
    });
}

/**
 * Routes a `csrf:error` event to the appropriate recovery surface.
 * @param {Object} [payload] - optional event payload; `hasBasketSnapshot` carries
 *   the true basket-snapshot presence reported by the failing CSRF-AjaxFail response
 * @returns {void}
 */
function dispatch(payload) {
    if (isDispatching) {
        return;
    }
    isDispatching = true;

    const hasBasketSnapshot = !!(payload && payload.hasBasketSnapshot);

    // Drop any spinner overlay left over from the failed AJAX call.
    removeVeil();

    const $csrfModal = getCsrfModal();

    if (!$csrfModal.length) {
        isDispatching = false;
        return;
    }

    const onCheckout = $('#checkout-main').length > 0;
    if (!onCheckout) {
        showLegacyVariant($csrfModal);
        return;
    }

    const customerType = $csrfModal.attr('data-customer-type');
    if (customerType === 'authenticated') {
        dispatchAuthenticatedRecovery();
    } else {
        // Close the "Sign in to checkout faster" modal if it is open so the
        // guest CSRF modal does not stack on top of it.
        // Defer showing the CSRF modal until the login modal has fully closed,
        // otherwise Bootstrap's shared backdrop teardown breaks the new modal.
        const $loginModal = getCheckoutLoginModal();
        if ($loginModal.length && $loginModal.hasClass('show')) {
            $loginModal.one('hidden.bs.modal', function () {
                dispatchGuestRecovery(hasBasketSnapshot);
            });
            $loginModal.modal('hide');
        } else {
            dispatchGuestRecovery(hasBasketSnapshot);
        }
    }
}

module.exports = {
    listenToCSRFErrors: function () {
        $('body').on('csrf:error', function (event, payload) {
            dispatch(payload);
        });

        $(document).on('ajaxError', function (event, jqXHR) {
            if (!jqXHR || jqXHR.csrfModalHandled) {
                return;
            }

            const responseJSON = jqXHR.responseJSON;

            if (responseJSON && responseJSON.csrfError) {
                // eslint-disable-next-line no-param-reassign
                jqXHR.csrfModalHandled = true;
                clarity.trackException(
                    responseJSON.exceptionType || clarity.EXCEPTION_TYPES.CSRF_FAILURE,
                    responseJSON.customSessionIdentifier
                );
                $('body').trigger('csrf:error', { hasBasketSnapshot: !!responseJSON.hasBasketSnapshot });
            }
        });

        $(document).on('login:success:gtm', function () {
            getCsrfModal().attr('data-customer-type', 'authenticated');
        });
    }
};
